import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { clsx } from "clsx";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuth, useEffectiveUser } from "../lib/auth";
import { useTheme } from "../lib/theme";
import { can, LEVEL_LABEL } from "../lib/permissions";
import type { Employee, UserLevel } from "../lib/types";
import { Modal } from "./ui/Modal";
import { SubscriptionBanner } from "./SubscriptionBanner";
import { NotificationBell } from "./NotificationBell";
import { IOSNativeMenu } from "./IOSNativeMenu";
import { AccountDeletionPanel } from "./AccountDeletionPanel";
import { useBetaDesign } from "../lib/platform";
import { Button } from "./ui/Button";
import { Avatar } from "./ui/Avatar";

const topNav = [
  { to: "/", label: "Dashboard", icon: "▦", end: true },
];

// Per-row `guard` returns true when the nav entry should appear for
// the current user. Pages that are otherwise visible to everyone in
// the agency have `guard: undefined`.
const bottomNav: Array<{
  to: string;
  label: string;
  icon: string;
  guard?: (u: ReturnType<typeof useAuth.getState>["user"]) => boolean;
}> = [
  { to: "/employees", label: "Employees", icon: "◉" },
  { to: "/shifts",    label: "Shifts",    icon: "◷" },
  { to: "/time-off",  label: "Time Off",  icon: "✦" },
  { to: "/training",  label: "Training",  icon: "✦" },
  { to: "/audit",     label: "Audit",     icon: "≡", guard: (u) => can.viewAudit(u) },
  // Settings is intentionally NOT in this list — it renders as
  // its own entry at the very bottom of the sidebar (below
  // On-Call) so preferences / account entries sit apart from the
  // day-to-day nav, matching the iOS menu convention.
];

export function Layout() {
  const user = useAuth((s) => s.user);
  // Effective user = real user with previewed level applied. Nav
  // guards use this so previewing as L4 actually hides Settings /
  // Audit just like it would for a real L4. The raw user stays
  // around for the "sign out as" display and the preview-toggle
  // itself so the previewer can always switch back.
  const effective = useEffectiveUser();
  const setUser = useAuth((s) => s.setUser);
  const previewLevel = useAuth((s) => s.previewLevel);
  const setPreviewLevel = useAuth((s) => s.setPreviewLevel);
  const qc = useQueryClient();
  const departments = useQuery({
    queryKey: ["departments"],
    queryFn: api.listDepartments,
  });
  const brand = useQuery({ queryKey: ["brandImage"], queryFn: api.getBrandImage });
  const employees = useQuery({ queryKey: ["employees"], queryFn: () => api.listEmployees() });
  const shifts = useQuery({ queryKey: ["shifts"], queryFn: () => api.listShifts() });
  // Tac mode is agency-wide, so the banner belongs at the layout
  // level rather than on each page that wants to surface it. Layout
  // renders it once at the very top of the chrome and pushes
  // everything (hamburger included) below it.
  const agency = useQuery({ queryKey: ["agency"], queryFn: api.getAgency });
  const tacModeActive = agency.data?.tacModeActive === true;
  const myEmployee: Employee | undefined =
    user?.employeeId
      ? employees.data?.find((e) => e.id === user.employeeId)
      : undefined;
  const myHomeShift = myEmployee
    ? shifts.data?.find((s) => s.id === myEmployee.homeShiftId)
    : undefined;
  const [profileOpen, setProfileOpen] = useState(false);
  // Mobile sidebar drawer. On >=md the sidebar is always visible
  // in its flex column; on mobile we hide it off-canvas and slide
  // it in when the hamburger is tapped.
  const [navOpen, setNavOpen] = useState(false);
  const theme = useTheme((s) => s.theme);
  const toggleTheme = useTheme((s) => s.toggle);
  const betaDesign = useBetaDesign();

  const linkClass = (isActive: boolean) =>
    clsx(
      "flex items-center gap-3 px-4 py-2 text-sm transition",
      isActive
        ? "bg-slate-800 text-white border-l-2 border-brand-500"
        : "text-slate-300 hover:bg-slate-800/60",
    );

  return (
    <div className="h-full flex flex-col">
      {/* Agency-wide tac-mode banner — sits flush against the top
        * of the layout body, which already sits below the iOS
        * safe-area thanks to body's global padding (see styles.css).
        * Print-hidden so the schedule's PDF export doesn't get a
        * stray banner. */}
      {tacModeActive && (
        <div
          className="shrink-0 border-b border-amber-300 bg-amber-50 px-3 py-1.5 text-xs leading-tight md:leading-normal font-semibold uppercase tracking-wider text-amber-800 text-center print:hidden"
        >
          {agency.data?.tacModeStartDate ? (
            <>
              <span className="md:hidden">
                12-HR SHIFTS — from {agency.data.tacModeStartDate}
              </span>
              <span className="hidden md:inline">
                12-hour Shifts scheduled — regular shifts before {agency.data.tacModeStartDate}, 12-hour shifts on/after.
              </span>
            </>
          ) : (
            <>
              <span className="md:hidden">12-HR SHIFTS</span>
              <span className="hidden md:inline">
                12-hour Shifts active — showing 12-hour shifts only. Regular schedule is hidden.
              </span>
            </>
          )}
        </div>
      )}
    <div className="flex-1 min-h-0 flex relative">
      {/* Mobile hamburger — absolute top-left of the layout's body
        * (the row below the tac banner). `absolute` not `fixed` so
        * the banner pushes it down naturally; the layout root
        * doesn't scroll (scrolling happens inside <main>), so the
        * hamburger still reads as "stuck to the top" while pages
        * scroll their content underneath. Visible only <md. */}
      <button
        type="button"
        onClick={() => setNavOpen(true)}
        className="md:hidden print:hidden absolute left-3 top-2 z-40 w-9 h-9 flex items-center justify-center rounded-md bg-white/95 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
        aria-label="Open navigation"
      >
        <span className="text-lg leading-none">☰</span>
      </button>

      {/* Notification inbox. Mirrors the hamburger placement on the
        * opposite side — always visible (web + iOS), pulls down a
        * popover with the user's recent notifications and an unread
        * badge. */}
      <NotificationBell />

      {/* Backdrop — tap to close the mobile drawer. Hidden when
        * the iOS beta menu is active; that sheet handles its own
        * full-screen presentation. */}
      {navOpen && !betaDesign && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/40"
          onClick={() => setNavOpen(false)}
          aria-hidden
        />
      )}

      {/* iOS beta full-screen menu sheet — only renders when the
        * beta-design flag is on AND we're inside the Capacitor
        * native shell. */}
      {betaDesign && (
        <IOSNativeMenu open={navOpen} onClose={() => setNavOpen(false)} />
      )}

      <aside
        className={clsx(
          "w-56 shrink-0 bg-slate-900 text-slate-100 flex flex-col",
          // Off-canvas drawer on mobile; in-flow static on md+.
          "fixed inset-y-0 left-0 z-50 transform transition-transform duration-200 ease-out",
          "md:static md:translate-x-0 md:transition-none md:z-auto",
          // Mobile drawer is fixed → ignores the body's safe-area
          // padding, so the close button at the top would sit
          // behind the iOS status bar / notch and the bottom links
          // would slip under the home indicator. Pad here on mobile
          // only; desktop's static layout already inherits the
          // body's safe-area padding.
          "pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] md:pt-0 md:pb-0",
          navOpen ? "translate-x-0" : "-translate-x-full",
          // When the iOS beta-design menu is active the regular
          // dark sidebar isn't used at all — IOSNativeMenu handles
          // navigation full-screen instead.
          betaDesign && "hidden",
        )}
      >
        <div className="px-4 py-4 border-b border-slate-800 flex items-center justify-between gap-2">
          <div>
            <div className="text-sm font-semibold tracking-wide">ShiftManager</div>
            <NavLink
              to="/changelog"
              className="text-xs text-slate-400 mt-0.5 font-mono hover:text-slate-200 hover:underline"
              title={`Built ${__BUILD_AT__} — view changelog`}
            >
              build {__BUILD_SHA__}
            </NavLink>
          </div>
          {/* Close button only on mobile. */}
          <button
            type="button"
            onClick={() => setNavOpen(false)}
            className="md:hidden w-7 h-7 flex items-center justify-center rounded text-slate-300 hover:bg-slate-800"
            aria-label="Close navigation"
          >
            <span className="text-lg leading-none">×</span>
          </button>
        </div>
        <nav
          className="flex-1 py-2 overflow-y-auto"
          onClick={() => setNavOpen(false)}
        >
          {topNav.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              className={({ isActive }) => linkClass(isActive)}
            >
              <span className="w-4 text-center text-slate-400">{it.icon}</span>
              {it.label}
            </NavLink>
          ))}

          <div className="mt-3 px-4 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            Schedules
          </div>
          {(departments.data ?? []).map((d) => (
            <NavLink
              key={d.id}
              to={`/schedule/${d.id}`}
              className={({ isActive }) => linkClass(isActive)}
              title={`Open ${d.name} schedule`}
            >
              <span className="w-4 text-center text-slate-400">▤</span>
              {d.name}
            </NavLink>
          ))}
          <NavLink
            to="/settings#departments"
            className="flex items-center gap-3 px-4 py-1.5 text-xs text-slate-400 hover:text-slate-200 italic"
          >
            <span className="w-4 text-center">+</span>
            New department
          </NavLink>

          <div className="mt-3 border-t border-slate-800" />
          {bottomNav
            .filter((it) => !it.guard || it.guard(effective))
            .map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                className={({ isActive }) => linkClass(isActive)}
              >
                <span className="w-4 text-center text-slate-400">{it.icon}</span>
                {it.label}
              </NavLink>
            ))}
          <NavLink
            to="/on-call"
            className={({ isActive }) => linkClass(isActive)}
            title="Specialty on-call rotations"
          >
            <span className="w-4 text-center text-slate-400">☏</span>
            On-Call
          </NavLink>
          {/* Settings sits at the very bottom of the nav, below
            * On-Call and separated from the day-to-day items above
            * — matches the iOS menu convention where preferences
            * live under the primary destinations, not among them. */}
          {can.viewSettings(effective) && (
            <NavLink
              to="/settings"
              className={({ isActive }) => linkClass(isActive)}
            >
              <span className="w-4 text-center text-slate-400">⚙</span>
              Settings
            </NavLink>
          )}
        </nav>
        {brand.data && (
          <div className="border-t border-slate-800 px-4 py-4 flex items-center justify-center">
            <img
              src={brand.data}
              alt="Agency image"
              className="max-h-32 w-40 object-contain opacity-95"
            />
          </div>
        )}
        <div className="border-t border-slate-800 p-3 text-xs text-slate-400 space-y-1.5">
          {myEmployee ? (
            <button
              onClick={() => setProfileOpen(true)}
              className="w-full text-left bg-slate-800/60 hover:bg-slate-800 border border-slate-700 rounded px-2 py-2 transition flex items-center gap-2"
              title="View my profile"
            >
              <Avatar
                photo={myEmployee.photo}
                firstName={myEmployee.firstName}
                lastName={myEmployee.lastName}
                size={36}
              />
              <div className="min-w-0 flex-1">
                <div className="text-slate-100 font-medium truncate">
                  {myEmployee.firstName} {myEmployee.lastName}
                </div>
                <div className="text-[11px] text-slate-400 truncate">
                  {myEmployee.rank ? `${myEmployee.rank} · ` : ""}
                  {myEmployee.badgeNumber ? `#${myEmployee.badgeNumber}` : ""}
                  {!myEmployee.rank && !myEmployee.badgeNumber && "View details"}
                </div>
                <div className="text-[11px] text-slate-500">
                  {LEVEL_LABEL[user!.level]}
                </div>
              </div>
            </button>
          ) : (
            <button
              onClick={() => setProfileOpen(true)}
              className="w-full text-left bg-slate-800/60 hover:bg-slate-800 border border-slate-700 rounded px-2 py-2 transition"
              title="View my profile"
            >
              <div className="text-slate-100 font-medium truncate">
                {user?.username ?? "—"}
              </div>
              <div className="text-[11px] text-slate-400">
                {user?.level ? LEVEL_LABEL[user.level] : ""}
              </div>
            </button>
          )}
          {can.previewAsOtherLevel(user) && (
            <label className="block">
              <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">
                Preview as
              </span>
              <select
                value={previewLevel ?? ""}
                onChange={(e) =>
                  setPreviewLevel(e.target.value ? (e.target.value as UserLevel) : null)
                }
                className={
                  "w-full bg-slate-800 border rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-sky-400 " +
                  (previewLevel
                    ? "border-amber-500 text-amber-200"
                    : "border-slate-700 text-slate-100")
                }
                title="Preview permissions as another level for testing"
              >
                <option value="">— My level —</option>
                {/* Only expose levels BELOW the current user's rank
                  * so a supervisor can never spoof up. Owner sees
                  * L1/L2/L3/L4, L1 sees L2/L3/L4, L2 sees L3/L4. */}
                {(["l1", "l2", "l3", "l4"] as const)
                  .filter((lvl) => can.canPreviewAsLevel(user, lvl))
                  .map((lvl) => (
                    <option key={lvl} value={lvl}>{LEVEL_LABEL[lvl]}</option>
                  ))}
              </select>
            </label>
          )}
          {previewLevel && (
            <div className="text-[10px] text-amber-300 bg-amber-900/30 border border-amber-700/50 rounded px-1.5 py-1">
              Previewing as {LEVEL_LABEL[previewLevel]}. UI gates match that
              level; writes still post as {user?.username}.
            </div>
          )}
          <div className="pt-1">
            <button
              type="button"
              onClick={toggleTheme}
              className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded border border-slate-700 hover:bg-slate-800 text-slate-200 text-xs"
              title={`Switch to ${theme === "dark" ? "day" : "dark"} mode`}
            >
              <span className="flex items-center gap-2">
                <span className="text-sm leading-none">
                  {theme === "dark" ? "☀" : "☾"}
                </span>
                <span>{theme === "dark" ? "Day mode" : "Dark mode"}</span>
              </span>
              <span className="text-[10px] uppercase tracking-wider text-slate-500">
                {theme === "dark" ? "Dark" : "Day"}
              </span>
            </button>
          </div>
          <div className="pt-1 flex justify-end">
            <button
              onClick={async () => {
                await api.logout();
                setUser(null);
                qc.clear();
              }}
              className="text-slate-400 hover:text-white underline-offset-2 hover:underline"
              title="Sign out"
            >
              Sign out
            </button>
          </div>
        </div>
      </aside>
      <main className="flex-1 min-w-0 overflow-auto pt-12 md:pt-12">
        <SubscriptionBanner />
        <Outlet />
      </main>
    </div>

      <Modal
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        title="My profile"
        footer={
          <Button variant="secondary" onClick={() => setProfileOpen(false)}>Close</Button>
        }
      >
        <div className="space-y-4 text-sm">
          {myEmployee && (
            <div className="space-y-4">
            <div className="flex items-center gap-4">
              <Avatar
                photo={myEmployee.photo}
                firstName={myEmployee.firstName}
                lastName={myEmployee.lastName}
                size={96}
              />
              <div>
                <div className="text-lg font-semibold text-slate-900">
                  {myEmployee.firstName} {myEmployee.lastName}
                </div>
                <div className="text-sm text-slate-500">
                  {myEmployee.rank || "—"}
                  {myEmployee.badgeNumber ? ` · #${myEmployee.badgeNumber}` : ""}
                </div>
              </div>
            </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2">
            <MyField label="Name" value={`${myEmployee.firstName} ${myEmployee.lastName}`} />
            <MyField label="Access level" value={LEVEL_LABEL[user!.level]} />
            <MyField label="Badge #" value={myEmployee.badgeNumber || "—"} />
            <MyField label="Rank" value={myEmployee.rank || "—"} />
            <MyField label="Status" value={myEmployee.status} />
            <MyField label="OIC" value={myEmployee.isOic ? "Yes" : "No"} />
            <MyField label="Hire date" value={myEmployee.hireDate || "—"} />
            <MyField
              label="Home shift"
              value={myHomeShift ? myHomeShift.name : "—"}
            />
            <MyField
              label="Emails"
              value={(myEmployee.emails ?? []).join(", ") || "—"}
              span
            />
            <MyField
              label="Phones"
              value={(myEmployee.phones ?? []).join(", ") || "—"}
              span
            />
            <p className="col-span-2 text-xs text-slate-500 pt-2 border-t border-slate-100">
              Name, rank, badge, level, and department are managed by a
              supervisor. Ask them to update this record if anything is
              wrong.
            </p>
          </div>
            </div>
          )}
          {/* App Store Guideline 5.1.1(v) — every signed-in user has
            * a path to request account deletion from inside the app.
            * Officers without Settings access reach it via this modal;
            * supervisors with Settings also see the same card there.
            */}
          <AccountDeletionPanel />
        </div>
      </Modal>
    </div>
  );
}

function MyField({ label, value, span }: { label: string; value: string; span?: boolean }) {
  return (
    <div className={span ? "col-span-2" : ""}>
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-slate-900 capitalize">{value}</div>
    </div>
  );
}
